import React, { useState } from 'react';
import { IconButton, InputAdornment, Popover, TextField } from '@mui/material';
import { CalendarMonthOutlined } from '@mui/icons-material';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { ptBR } from '@mui/x-date-pickers/locales';
import 'moment/locale/pt-br';
import {
  dateError,
  displayPatientDate,
  parsePatientDate,
} from './PatientFormValidation';

type Props = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  error?: string | boolean;
  submitted?: boolean;
};

export const PatientDateInput = ({
  label,
  value,
  onChange,
  onBlur,
  disabled,
  error,
  submitted,
}: Props) => {
  const [touched, setTouched] = useState(false);
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const message = touched || submitted ? dateError(value) : '';
  const selected = parsePatientDate(value);
  return (
    <>
      <TextField
        label={label}
        size="small"
        fullWidth
        disabled={disabled}
        placeholder="DD/MM/AAAA"
        value={
          /^\d{4}-\d{2}-\d{2}(?:T|$)/.test(value)
            ? displayPatientDate(value)
            : value
        }
        onChange={(event) => onChange(event.target.value)}
        onBlur={() => {
          setTouched(true);
          if (!dateError(value)) onChange(displayPatientDate(value));
          onBlur?.();
        }}
        error={Boolean(error || message)}
        helperText={message || (typeof error === 'string' ? error : '')}
        inputProps={{ maxLength: 10, inputMode: 'numeric' }}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                type="button"
                disabled={disabled}
                aria-label={`Abrir calendário: ${label}`}
                onClick={(event) => setAnchor(event.currentTarget)}
              >
                <CalendarMonthOutlined />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <LocalizationProvider
          dateAdapter={AdapterMoment}
          adapterLocale="pt-br"
          localeText={
            ptBR.components.MuiLocalizationProvider.defaultProps.localeText
          }
        >
          <DateCalendar
            value={selected.isValid() ? selected : null}
            disableFuture
            onChange={(date) => {
              if (!date?.isValid()) return;
              onChange(date.format('DD/MM/YYYY'));
              setTouched(true);
              setAnchor(null);
            }}
          />
        </LocalizationProvider>
      </Popover>
    </>
  );
};
